import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../../context/FiltersContext.tsx';
import { getPrecisionRegex, integerRegex } from '../../constants/regex.ts';
import type { NumericInputConfigurableFilter } from '../../types.ts';

export type NumericInputSelectorProps = {
  filterKey: string;
  selectedOptions?: string;
  onOptionChange: (value: string) => void;
  setValidationState?: (value: boolean) => void;
} & Pick<NumericInputConfigurableFilter<unknown>, 'config'>;

export const NumericInputSelector = ({
  filterKey,
  selectedOptions,
  onOptionChange,
  config,
  setValidationState,
}: NumericInputSelectorProps) => {
  const { t } = useTranslation();
  const { filtersErrors, setFiltersErrors } = useFiltersContext();
  const [filterVal, setFilterVal] = useState<string>(selectedOptions ?? '');

  const { fieldPlaceholder, startAdornment, endAdornment, inputType, precision, customRegex } = config ?? {};

  const validateValue = useCallback(
    (val: string, isOnChange: boolean) => {
      const isFloat = inputType === 'float';

      const precisionRegex = getPrecisionRegex(precision ?? 0);
      const regex = customRegex ?? (isFloat ? precisionRegex : integerRegex);
      const isNotNegative = !/^-\d*\.?\d*$/.test(val);
      const isValid = val === '' || (regex.test(val) && isNotNegative);
      const hasInvalidLeadingZeros = /^0\d+/.test(val) && !/^0(\.|$)/.test(val);

      if (!isValid || hasInvalidLeadingZeros) {
        setFiltersErrors(prev => ({
          ...prev,
          [filterKey]: t('Invalid value'),
        }));
        setValidationState?.(true);
      } else {
        setFiltersErrors(prev => {
          const { [filterKey]: _, ...rest } = prev;
          return rest;
        });
        setValidationState?.(false);

        if (isOnChange) {
          onOptionChange(val.trim());
        }
      }
    },
    [inputType, precision, customRegex, filterKey, setFiltersErrors, setValidationState, t, onOptionChange]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      const val = e.target.value;
      setFilterVal(val);
      validateValue(val, true);
    },
    [validateValue]
  );

  useEffect(() => {
    if (filterVal !== '') {
      validateValue(filterVal, false);
    }
  }, [filterVal, validateValue]);

  return (
    <Stack
      sx={{
        alignItems: 'flex-start',
        gap: 1,
      }}
    >
      {config?.label && (
        <Typography variant="body2" data-test="numeric-input-filter-label" sx={{ paddingLeft: 1.5 }}>
          {config.label}
        </Typography>
      )}
      <TextField
        data-test={`numeric-input-filter-${filterKey}-input`}
        variant="outlined"
        size="small"
        value={filterVal}
        onChange={handleChange}
        placeholder={fieldPlaceholder ?? t('Enter value')}
        slotProps={{
          htmlInput: {
            inputMode: inputType === 'float' ? 'decimal' : 'numeric',
            type: 'text',
            step: precision ? `0.${'0'.repeat(precision - 1)}1` : 1,
            sx: {
              marginLeft: 0.5,
              marginRight: 0.5,
            },
          },
          input: {
            startAdornment,
            endAdornment,
          },
        }}
        sx={{ width: '100%' }}
        error={!!filtersErrors[filterKey]}
        helperText={filtersErrors[filterKey]}
      />
    </Stack>
  );
};
